import React, { useMemo, useState } from 'react';
import { FlexTableV2 } from 'components/FlexTableV2';
import { getSortedRowModel } from '@tanstack/react-table';
import { Line, Pie } from 'react-chartjs-2';
import * as S from '../styles';
import { matrixColumnsConfig } from '../logic';

const generateColors = (count) => {
  const colors = [
    '#007bff',
    '#6610f2',
    '#6f42c1',
    '#e83e8c',
    '#dc3545',
    '#fd7e14',
    '#ffc107',
    '#28a745',
    '#20c997',
    '#17a2b8'
  ];
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

export const AnalyticsDashboard = ({
  monitoringData,
  tableState,
  layoutOption
}) => {
  const reasonData = monitoringData.REASON_RANKING || [];
  const originData = monitoringData.START_CHATS_COUNT || { BOT: 0, HUMAN: 0 };
  const matrixData = monitoringData.MATRIX_MAP_HOUR || [];
  const matrixColumns = useMemo(() => matrixColumnsConfig, []);

  const [selectedReason, setSelectedReason] = useState(null);

  const topReasonData = useMemo(() => {
    return reasonData.slice(0, 10);
  }, [reasonData]);

  const pieChartData = useMemo(() => {
    const labels = topReasonData.map((r) => r.name);
    const data = topReasonData.map((r) => r.count);
    const backgroundColors = generateColors(topReasonData.length);

    const finalBackgroundColors = backgroundColors.map((color, index) => {
      const reasonName = topReasonData[index]?.name;
      if (selectedReason && selectedReason.name !== reasonName) {
        return color + '33';
      }
      return color;
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: finalBackgroundColors,
          hoverBackgroundColor: finalBackgroundColors
        }
      ]
    };
  }, [topReasonData, selectedReason]);

  const pieOptions = {
    legend: {
      display: true,
      position: 'right',
      labels: {
        fontSize: 18,
        boxWidth: 12,
        padding: 10
      }
    },
    onClick: (e, elements) => {
      if (elements && elements.length > 0) {
        const index = elements[0]._index;
        const reason = topReasonData[index];
        setSelectedReason(reason);
      } else {
        setSelectedReason(null);
      }
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          const label = data.labels[tooltipItem.index];
          const value = data.datasets[0].data[tooltipItem.index];
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1) + '%';
          return `${label}: ${value} (${percentage})`;
        }
      }
    }
  };

  const lineChartData = useMemo(() => {
    const sortedMatrix = [...matrixData];
    const labels = sortedMatrix.map((item) => {
      const val = item.hourRange || '';
      if (val.includes(' - ')) return val;
      const start = parseInt(val, 10);
      if (!isNaN(start)) {
        const end = (start + 1) % 24;
        const fmt = (n) => n.toString().padStart(2, '0');
        return `${fmt(start)}:00 - ${fmt(end)}:00`;
      }
      return val;
    });

    const realizedVolume = sortedMatrix.map(
      (item) => item.projectedVolume.realizedVolume || 0
    );

    const projectedVolume = sortedMatrix.map(
      (item) => item.projectedVolume.projectedVolume || 0
    );

    return {
      labels,
      datasets: [
        {
          label: 'Volume Realizado',
          data: realizedVolume,
          fill: false,
          borderColor: 'rgb(19,133,77)',
          backgroundColor: 'rgb(19,133,77)',
          tension: 0.3
        },
        {
          label: 'Volume Projetado',
          data: projectedVolume,
          fill: false,
          borderColor: '#11cdef',
          backgroundColor: '#11cdef',
          borderDash: [5, 5],
          tension: 0.3
        }
      ]
    };
  }, [matrixData]);

  const lineOptions = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true
          },
          gridLines: {
            color: '#f0f0f0'
          }
        }
      ],
      xAxes: [
        {
          gridLines: {
            display: false
          }
        }
      ]
    },
    legend: {
      position: 'bottom'
    }
  };

  const filteredMatrixData = useMemo(() => {
    return matrixData.filter(
      (item) => (item.chatsAttended || 0) > 0 || (item.closedChats || 0) > 0
    );
  }, [matrixData]);

  const allSortedSubReasons = useMemo(() => {
    const all = reasonData.flatMap((r) =>
      (r.subReasons || []).map((sub) => ({
        ...sub,
        displayName: `${r.name} - ${sub.name}`
      }))
    );

    return all.sort((a, b) => b.count - a.count);
  }, [reasonData]);

  const displayedSubReasons = useMemo(() => {
    if (selectedReason) {
      return (selectedReason.subReasons || []).sort(
        (a, b) => b.count - a.count
      );
    }
    return allSortedSubReasons;
  }, [selectedReason, allSortedSubReasons]);

  return (
    <S.AnalyticsSection>
      <S.MatrixTableWrapper>
        <h4>Matriz Horária de Indicadores</h4>
        <div className="flex-table-container">
          <FlexTableV2
            table={{
              columns: matrixColumns,
              data: filteredMatrixData,
              rowCount: filteredMatrixData.length,
              getSortedRowModel: getSortedRowModel(),

              enablePagination: false,
              enableColumnOrdering: true,
              enableHiding: true,

              manualSorting: false,
              enableLocalStorageMemoWithKey: 'monitoring-chat-matrix',
              scrollContainerHeight: 'unset'
            }}
            state={tableState}
          />
        </div>
      </S.MatrixTableWrapper>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ flex: 2 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}
          >
            <h4>Ranking de Motivos (Top 10 - Gráfico)</h4>
            {selectedReason && (
              <button
                onClick={() => setSelectedReason(null)}
                style={{
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  textDecoration: 'underline'
                }}
              >
                Limpar filtro
              </button>
            )}
          </div>

          <div style={{ flex: 1, minHeight: '250px' }}>
            {reasonData.length > 0 ? (
              <Pie data={pieChartData} options={pieOptions} />
            ) : (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#888'
                }}
              >
                Sem dados de motivos
              </div>
            )}
          </div>
        </S.AnalyticsCard>

        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>Ranking de Motivos (Top 10 - Lista)</h4>
          <S.RankingList>
            {topReasonData && topReasonData.length > 0 ? (
              topReasonData.map((reason, idx) => (
                <li
                  key={idx}
                  style={{
                    padding: '0.4rem 0',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    backgroundColor:
                      selectedReason?.name === reason.name
                        ? '#f0f8ff'
                        : 'transparent'
                  }}
                  onClick={() => setSelectedReason(reason)}
                >
                  <span className="label" style={{ fontWeight: 'normal' }}>
                    {reason.name}
                  </span>
                  <span className="value" style={{ fontSize: '0.8rem' }}>
                    {reason.count}
                  </span>
                </li>
              ))
            ) : (
              <li style={{ color: '#999', fontStyle: 'italic' }}>
                Nenhum motivo
              </li>
            )}
          </S.RankingList>
        </S.AnalyticsCard>
      </S.AnalyticsRow>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>
            Submotivos:{' '}
            <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
              {selectedReason ? selectedReason.name : 'Geral'}
            </span>
          </h4>
          <S.RankingList>
            {displayedSubReasons && displayedSubReasons.length > 0 ? (
              displayedSubReasons.slice(0, 10).map((sub, idx) => (
                <li
                  key={idx}
                  style={{ padding: '0.4rem 0', fontSize: '0.85rem' }}
                >
                  <span className="label" style={{ fontWeight: 'normal' }}>
                    {sub.displayName || sub.name}
                  </span>
                  <span className="value" style={{ fontSize: '0.8rem' }}>
                    {sub.count}
                  </span>
                </li>
              ))
            ) : (
              <li style={{ color: '#999', fontStyle: 'italic' }}>
                Nenhum submotivo
              </li>
            )}
          </S.RankingList>
        </S.AnalyticsCard>

        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>Origem dos Chats</h4>
          <S.RankingList>
            <li>
              <span className="label">Fluxo Automático (Bot)</span>
              <span className="value">{originData.BOT}</span>
            </li>
            <li>
              <span className="label">Fluxo Geral</span>
              <span className="value">{originData.HUMAN}</span>
            </li>
          </S.RankingList>
        </S.AnalyticsCard>
      </S.AnalyticsRow>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ width: '100%' }}>
          <h4>Volume Projetado x Realizado</h4>
          <div style={{ height: '300px', width: '100%' }}>
            <Line
              key={layoutOption}
              data={lineChartData}
              options={lineOptions}
            />
          </div>
        </S.AnalyticsCard>
      </S.AnalyticsRow>
    </S.AnalyticsSection>
  );
};
